import { noop, type QueryClient } from "@tanstack/react-query"
import type { RegisteredRouter } from "@tanstack/react-router"
import { characterLastMessageQueryOptions } from "@/entities/characters"
import { friendsQueryOptions } from "@/entities/friend"
import { revalidateUser, userQueryOptions } from "@/entities/user"
import { captureError } from "@/shared/lib/diagnostics"
import { restoreSnapshot } from "./restore"
import { dropSnapshot } from "./storage"

export async function bootFromSnapshot(
	router: RegisteredRouter,
): Promise<boolean> {
	try {
		const userId = await restoreSnapshot(router.options.context.queryClient)
		if (userId === null) return false

		void revalidate(router, userId).catch((error) => {
			captureError(error, { scope: "bootSnapshot.revalidate" })
		})
		return true
	} catch (error) {
		captureError(error, { scope: "bootSnapshot.restore" })
		return false
	}
}

async function revalidate(
	router: RegisteredRouter,
	userId: number,
): Promise<void> {
	const { queryClient } = router.options.context

	void queryClient
		.query({
			...friendsQueryOptions(userId),
			staleTime: 0,
		})
		.catch(noop)

	const revalidation = await revalidateUser(queryClient)
	if (revalidation === "unreachable") return

	if (revalidation === "lost") dropSnapshot()
	// The confirmed user can have another character than the snapshot.
	if (revalidation === "confirmed") refetchLastMessage(queryClient, userId)
	await router.invalidate()
}

function refetchLastMessage(queryClient: QueryClient, userId: number): void {
	const user = queryClient.getQueryData(userQueryOptions(userId).queryKey)
	if (!user) return
	void queryClient
		.query({
			...characterLastMessageQueryOptions(user.character, userId),
			staleTime: 0,
		})
		.catch(noop)
}
